import { ChevronRight } from 'lucide-react';
import { Tabs as T } from 'radix-ui';
import { Fragment, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface Crumb {
  readonly label: ReactNode;
  /** Rendered link for all but the current page. */
  readonly link?: ReactNode;
}

export function Breadcrumb(props: { items: readonly Crumb[]; label: string; className?: string }) {
  return (
    <nav aria-label={props.label} className={props.className}>
      <ol className="flex min-w-0 items-center gap-1.5 text-sm text-ink-muted">
        {props.items.map((item, index) => {
          const current = index === props.items.length - 1;
          return (
            <Fragment key={index}>
              {index > 0 ? (
                <ChevronRight
                  className="bl-flip-rtl size-3.5 shrink-0 text-ink-subtle"
                  aria-hidden="true"
                />
              ) : null}
              <li
                className={cn('min-w-0 truncate', current && 'font-medium text-ink')}
                aria-current={current ? 'page' : undefined}
              >
                {current ? item.label : (item.link ?? item.label)}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

export function Tabs(props: {
  value: string;
  onValueChange: (value: string) => void;
  tabs: readonly { value: string; label: ReactNode; content: ReactNode }[];
  label: string;
}) {
  return (
    <T.Root value={props.value} onValueChange={props.onValueChange}>
      <T.List aria-label={props.label} className="flex gap-1 border-b border-line">
        {props.tabs.map((tab) => (
          <T.Trigger
            key={tab.value}
            value={tab.value}
            className="-mb-px border-b-2 border-transparent px-3 py-2 text-sm font-medium text-ink-muted hover:text-ink data-[state=active]:border-primary data-[state=active]:text-ink"
          >
            {tab.label}
          </T.Trigger>
        ))}
      </T.List>
      {props.tabs.map((tab) => (
        <T.Content key={tab.value} value={tab.value} className="pt-4 outline-none">
          {tab.content}
        </T.Content>
      ))}
    </T.Root>
  );
}
